import { logger } from 'module/logger';
import {
  CompanyPageSourceDerivedSectionAttributes,
  CustomSectionAttributes,
  CustomSectionNode,
  CustomSectionNodeType,
} from 'api/custom-section/types';
import { PageTemplateBuilderAPI } from 'api/page-template/builder';
import {
  JSONContent,
  PageTemplateLite,
  PageType,
} from 'api/page-template/types';
import { TiptapJson } from 'api/page-template/types';
import { v4 } from 'uuid';
import {
  isCompanyPageSourceDerivedSection,
  isCustomSectionNode,
} from 'components/organisms/document-editor/extensions/custom-section/utils/node-utils';
import { fragmentJSONToEditorContent } from 'components/organisms/document-editor/utils/node-utils';
import { TiptapJsonState } from '../types';

export const isCompanyPageSourceSection = (
  node: JSONContent,
): node is CustomSectionNode<CompanyPageSourceDerivedSectionAttributes> => {
  return isCustomSectionNode(node) && isCompanyPageSourceDerivedSection(node);
};
const filterInvalidSection = (node: JSONContent) => {
  if (isCompanyPageSourceSection(node)) {
    return node.attrs.config.sourcePageTemplateSections.length !== 0;
  }

  return true;
};

export const removeInvalidSections = (tiptapJson: TiptapJson): TiptapJson => {
  return {
    ...tiptapJson,
    content: tiptapJson.content.filter(filterInvalidSection),
  };
};

export const removeSourceFromTiptapJson = (
  tiptapJson: TiptapJson,
  sourcesToBeRemoved: PageTemplateLite[],
): TiptapJson => {
  const sourceIdsToBeRemoved = sourcesToBeRemoved.map(
    (source) => source.templateId,
  );

  const updatedContent = tiptapJson.content
    .map((node) => {
      if (isCompanyPageSourceSection(node)) {
        return {
          ...node,
          attrs: {
            ...node.attrs,
            sourcePageTemplateSections:
              node.attrs.config.sourcePageTemplateSections.filter(
                (section) =>
                  !sourceIdsToBeRemoved.includes(section.pageTemplateId),
              ),
          },
        };
      }

      return node;
    })
    .filter(filterInvalidSection);

  return fragmentJSONToEditorContent(updatedContent);
};
/* c8 ignore start - async function tested via integration tests */
export const fetchSectionsForSource = async (
  nextSources: PageTemplateLite[],
  oldSources: PageTemplateLite[],
  oldTiptapJson: TiptapJson,
): Promise<CustomSectionAttributes[]> => {
  return await PageTemplateBuilderAPI.getUpdatedSectionsForNewSources(
    {
      newSourceIds: nextSources.map((source) => source.templateId),
      oldSourceIds: oldSources.map((source) => source.templateId),
      sections: oldTiptapJson.content
        .filter(isCustomSectionNode)
        .map((node) => node.attrs),
    },
    PageType.Company,
  );
};
/* c8 ignore stop */
export const replaceTiptapJsonWithNewSources = async (
  nextSources: PageTemplateLite[],
  oldSources: PageTemplateLite[],
  oldTiptapJson: TiptapJson,
): Promise<TiptapJson> => {
  const sections = await fetchSectionsForSource(
    nextSources,
    oldSources,
    oldTiptapJson,
  );

  const withTemporaryIds = sections.map(
    (section) =>
      ({
        type: CustomSectionNodeType.RlCustomSection,
        attrs: {
          ...section,
          templateSectionId: v4(),
        },
      }) as CustomSectionNode,
  );

  return fragmentJSONToEditorContent(withTemporaryIds);
};
export const getModifiedSources = (
  oldSources: PageTemplateLite[],
  newSources: PageTemplateLite[],
) => {
  const oldSourceIds = oldSources.map((source) => source.templateId);
  const newSourceIds = newSources.map((source) => source.templateId);
  const addedSources = newSources.filter(
    (newSource) => !oldSourceIds.includes(newSource.templateId),
  );
  const removedSources = oldSources.filter(
    (oldSource) => !newSourceIds.includes(oldSource.templateId),
  );

  return {
    addedSources,
    removedSources,
    isModified: addedSources.length > 0 || removedSources.length > 0,
  };
};

export const getModifiedTiptapJsonForNewSources = async (
  nextSources: PageTemplateLite[],
  oldSources: PageTemplateLite[],
  oldTiptapJson: TiptapJson,
): Promise<TiptapJson> => {
  try {
    const { isModified } = getModifiedSources(oldSources, nextSources);

    if (isModified) {
      return await replaceTiptapJsonWithNewSources(
        nextSources,
        oldSources,
        oldTiptapJson,
      );
    }

    return oldTiptapJson;
  } catch (error) {
    logger.error('error in generating tiptap json from sources', { error });

    return oldTiptapJson;
  }
};
export const isTiptapJsonLoading = (tiptapJson: TiptapJsonState): boolean => {
  return tiptapJson.loading;
};
export const getTipTapJsonFromState = (
  tiptapJson: TiptapJsonState,
): TiptapJson => {
  if (tiptapJson.loading) {
    return tiptapJson.previousJson;
  }

  return tiptapJson.json;
};
